import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import TextInput from '../../../components/common/inputs/textInput.jsx';
import SelectInput from '../../../components/common/inputs/selectInput.jsx';
import NotificationManager from '../../../utils/notificationManager';
import ModalManager from '../../../components/common/modal/ModalManager';
import { isUnknownApiEndpointError } from '../../../utils/networkErrorUtils';

const defaultFormState = {
  parent: 'eth0',
  vid: '',
  mode: 'static',
  ipAddr: '',
  gateway: '',
  priority: '',
};

const styles = {
  card: {
    borderRadius: 8,
    padding: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  description: {
    marginTop: '8px',
    color: '#607284',
    fontSize: '14px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  banner: {
    marginTop: '16px',
    padding: '12px 16px',
    borderRadius: 6,
    fontSize: '13px',
  },
  warningBanner: {
    background: '#fef3c7',
    color: '#92400e',
  },
  infoBanner: {
    background: '#e0f2fe',
    color: '#075985',
  },
  dangerText: {
    color: '#b91c1c',
    fontSize: '13px',
    marginTop: '12px',
  },
  tableWrap: {
    marginTop: '20px',
  },
  secondaryButton: {
    background: '#e5e7eb',
    color: '#1f2937',
  },
  mutedButton: {
    background: '#f3f4f6',
    color: '#4b5563',
    border: '1px solid #d1d5db',
  },
};

const SkeletonBar = ({ width = '100%', height = 14, style = {} }) => (
  <div
    className="skeleton-blank"
    style={{
      width,
      height,
      borderRadius: '999px',
      ...style,
    }}
  />
);

const VLAN_SKELETON_COLUMNS = [
  { flex: 2, width: '70%' },
  { flex: 2, width: '50%' },
  { flex: 3, width: '85%' },
  { flex: 4, width: '90%' },
  { flex: 4, width: '80%' },
  { flex: 2, width: '55%' },
  { flex: 3, width: '65%' },
  { flex: 2, width: '60%', isAction: true },
];

const VLAN_SKELETON_SMALL_WIDTHS = ['65%', '55%', '85%', '80%', '60%', '50%', '70%'];

const VLAN_SKELETON_ROWS = 3;

const validateIpv4 = (value = '', required = false) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return required ? 'Gateway is required' : '';
  }
  const ipRegex =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return ipRegex.test(trimmed) ? '' : 'Invalid IPv4 address';
};

const validateCidr = (value = '') => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'IP address is required';
  }
  const parts = trimmed.split('/');
  if (parts.length !== 2) {
    return 'CIDR notation required (example: 192.0.2.10/24)';
  }
  const [ip, mask] = parts;
  const ipErr = validateIpv4(ip, true);
  if (ipErr) {
    return ipErr;
  }
  const maskVal = Number(mask);
  if (!Number.isInteger(maskVal) || maskVal < 1 || maskVal > 32) {
    return 'CIDR prefix must be between 1 and 32';
  }
  return '';
};

const formatIpDisplay = (vlan) => {
  const runtimeIp = (vlan?.status?.runtimeIp || '').trim();
  if (runtimeIp) {
    return vlan.mode === 'dhcp' ? `${runtimeIp} (DHCP)` : runtimeIp;
  }
  if (vlan.mode === 'dhcp') {
    return 'DHCP';
  }
  if (vlan.ipAddr) {
    return vlan.ipAddr;
  }
  return '—';
};

const formatGatewayDisplay = (vlan) =>
  (vlan?.status?.runtimeGateway || vlan?.gateway || '').trim() || '—';

const formatLinkStatus = (vlan) => {
  const linkUp = vlan?.status?.linkUp;
  if (linkUp === undefined || linkUp === null) {
    return '—';
  }
  return linkUp ? 'Up' : 'Down';
};

const vlanKey = (vlan) => `${vlan.parent || 'parent'}-${vlan.vid || 'vid'}`;

const VLAN_UNSUPPORTED_MESSAGE =
  'This PDU must be updated to a newer firmware version to manage VLAN interfaces from Synaccess Cloud.';

export default function DeviceConfigurationVlans({
  device,
  user,
  deviceOffline,
}) {
  const serial = device?.enclosureSerialNumber;
  const [vlans, setVlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletingKey, setDeletingKey] = useState('');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [featureMessage, setFeatureMessage] = useState('');
  const [initializing, setInitializing] = useState(true);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    }),
    []
  );

  const canModify =
    (user?.role?.editDeviceInfo ?? true) !== false &&
    !deviceOffline &&
    !featureUnavailable;
  const modalOptions = useMemo(
    () => ({
      preventOutsideClose: true,
      modalStyle: {
        minWidth: '540px',
        width: '660px',
        maxWidth: '90vw',
      },
      contentStyle: {
        maxHeight: '80vh',
        overflowY: 'auto',
      },
    }),
    []
  );

  const fetchVlans = useCallback(async () => {
    if (!serial) {
      setInitializing(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `/v1/devices/${serial}/network/vlans`,
        { headers: authHeaders }
      );
      const payload = data?.data;
      const nextVlans = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.vlans)
        ? payload.vlans
        : [];
      setVlans(nextVlans);
      setFeatureUnavailable(false);
      setFeatureMessage('');
    } catch (err) {
      console.error(err);
      if (isUnknownApiEndpointError(err)) {
        setFeatureUnavailable(true);
        setFeatureMessage(VLAN_UNSUPPORTED_MESSAGE);
        setVlans([]);
        setError('');
        return;
      }
      const message =
        err?.response?.data?.message ||
        'Unable to retrieve VLAN interfaces from the device.';
      setError(message);
      NotificationManager.alert(message);
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  }, [serial, authHeaders]);

  useEffect(() => {
    fetchVlans();
  }, [fetchVlans]);

  const handleCreateVlan = useCallback(
    async (payload) => {
      await axios.post(
        `/v1/devices/${serial}/network/vlans`,
        payload,
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      NotificationManager.success('VLAN interface created successfully.');
      fetchVlans();
    },
    [authHeaders, fetchVlans, serial]
  );

  const openCreateModal = useCallback(() => {
    if (!canModify) {
      return;
    }
    ModalManager.confirm(
      '',
      'Create VLAN',
      '',
      (status, closeModal) => {
        if (status && typeof closeModal === 'function') {
          closeModal();
        }
      },
      ({ close }) => (
        <VlanCreateForm
          canModify={canModify}
          deviceOffline={deviceOffline}
          onSubmit={handleCreateVlan}
          close={close}
        />
      ),
      true,
      true,
      modalOptions
    );
  }, [canModify, deviceOffline, handleCreateVlan, modalOptions]);

  const handleRefresh = () => {
    if (!loading) {
      fetchVlans();
    }
  };


  const handleDelete = (vlan) => {
    if (!serial || !canModify) {
      return;
    }
    const key = vlanKey(vlan);
    ModalManager.confirm(
      `Delete VLAN ${vlan.interface || `${vlan.parent}.${vlan.vid}` }?`,
      'Delete VLAN',
      'Delete',
      async (status, close) => {
        if (status) {
          try {
            setDeletingKey(key);
            await axios.delete(
              `/v1/devices/${serial}/network/vlans/${encodeURIComponent(vlan.parent)}/${encodeURIComponent(vlan.vid)}`,
              { headers: authHeaders }
            );
            NotificationManager.success('VLAN deleted successfully.');
            fetchVlans();
          } catch (err) {
            console.error(err);
            const message =
              err?.response?.data?.message || 'Failed to delete the VLAN.';
            NotificationManager.alert(message);
          } finally {
            setDeletingKey('');
            if (close) close();
          }
        } else if (close) {
          close();
        }
      }
    );
  };

  const renderVlanRow = (vlan) => {
    const key = vlanKey(vlan);
    const isDeleting = deletingKey === key;
    return (
      <div className="grid-row-wrapper" key={key}>
        <div className="grid-row large-only">
          <div style={{ flex: 2 }}>{vlan.parent}</div>
          <div style={{ flex: 2 }}>{vlan.vid}</div>
          <div style={{ flex: 3 }}>{vlan.interface || `${vlan.parent}.${vlan.vid}`}</div>
          <div style={{ flex: 4 }}>{formatIpDisplay(vlan)}</div>
          <div style={{ flex: 4 }}>{formatGatewayDisplay(vlan)}</div>
          <div style={{ flex: 2 }}>{vlan.priority || '—'}</div>
          <div style={{ flex: 3 }}>{formatLinkStatus(vlan)}</div>
          <div style={{ flex: 2 }}>
            {canModify ? (
              <button
                type="button"
                className="button danger"
                style={{ padding: '6px 12px' }}
                disabled={isDeleting}
                onClick={() => handleDelete(vlan)}
              >
                {isDeleting ? 'Removing…' : 'Delete'}
              </button>
            ) : (
              '—'
            )}
          </div>
        </div>
        <div className="small-only" style={{ background: '#fff', padding: '10px', border: '1px solid rgba(0,0,0,0.1)', borderTop: 'none' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {vlan.interface || `${vlan.parent}.${vlan.vid}`}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Parent:</strong> {vlan.parent}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>VLAN ID:</strong> {vlan.vid}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>IP / Mode:</strong> {formatIpDisplay(vlan)}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Gateway:</strong> {formatGatewayDisplay(vlan)}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Priority:</strong> {vlan.priority || '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 8 }}>
            <strong>Status:</strong> {formatLinkStatus(vlan)}
          </div>
          {canModify && (
            <button
              type="button"
              className="button danger"
              style={{ width: '100%' }}
              disabled={isDeleting}
              onClick={() => handleDelete(vlan)}
            >
              {isDeleting ? 'Removing…' : 'Delete VLAN'}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (featureUnavailable) {
    return (
      <div style={{ ...styles.card, ...styles.infoBanner }}>
        {featureMessage || VLAN_UNSUPPORTED_MESSAGE}
      </div>
    );
  }

  const renderSkeletonRows = () => (
    <>
      {Array.from({ length: VLAN_SKELETON_ROWS }).map((_, rowIdx) => (
        <div className="grid-row-wrapper" key={`vlan-skeleton-${rowIdx}`}>
          <div className="grid-row large-only">
            {VLAN_SKELETON_COLUMNS.map(({ flex, width, isAction }, colIdx) => (
              <div style={{ flex }} key={`vlan-skeleton-col-${colIdx}`}>
                <SkeletonBar
                  width={width}
                  height={isAction ? 30 : 14}
                  style={{ marginBottom: '8px' }}
                />
              </div>
            ))}
          </div>
          <div
            className="small-only"
            style={{
              background: '#fff',
              padding: '10px',
              border: '1px solid rgba(0,0,0,0.1)',
              borderTop: 'none',
            }}
          >
            {VLAN_SKELETON_SMALL_WIDTHS.map((width, idx) => (
              <div
                key={`vlan-skeleton-small-${idx}`}
                style={{ marginBottom: idx === VLAN_SKELETON_SMALL_WIDTHS.length - 1 ? 10 : 8 }}
              >
                <SkeletonBar width={width} />
              </div>
            ))}
            <SkeletonBar width="100%" height={32} />
          </div>
        </div>
      ))}
    </>
  );

  const showSkeleton = initializing || loading;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h4 style={styles.title}>802.1Q VLAN Interfaces</h4>
          <p style={styles.description}>
            Create VLAN subinterfaces on Ethernet uplinks and review their runtime status.
          </p>
        </div>
        <div style={styles.controls}>
          {canModify && (
            <button type="button" className="button" onClick={openCreateModal}>
              Create VLAN
            </button>
          )}
          <button
            type="button"
            className="button secondary"
            style={styles.secondaryButton}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {deviceOffline && (
        <div style={{ ...styles.banner, ...styles.warningBanner }}>
          Device is offline. Configuration changes are disabled until it reconnects.
        </div>
      )}

      {error && (
        <div className="alert alert-warning" style={{ marginTop: '16px' }}>
          {error}
        </div>
      )}

      <div style={styles.tableWrap}>
        <>
          <div className="grid-row header large-only">
            <div style={{ flex: 2 }}>Parent</div>
            <div style={{ flex: 2 }}>VLAN ID</div>
            <div style={{ flex: 3 }}>Interface</div>
            <div style={{ flex: 4 }}>IP / Mode</div>
            <div style={{ flex: 4 }}>Gateway</div>
            <div style={{ flex: 2 }}>Priority</div>
            <div style={{ flex: 3 }}>Link</div>
            <div style={{ flex: 2 }}>Actions</div>
          </div>
          {showSkeleton ? (
            renderSkeletonRows()
          ) : vlans.length === 0 ? (
            <div className="app-item">No VLAN interfaces are configured.</div>
          ) : (
            vlans.map(renderVlanRow)
          )}
        </>
      </div>
    </div>
  );
}

const VlanCreateForm = ({ canModify, deviceOffline, onSubmit, close }) => {
  const [formValues, setFormValues] = useState(defaultFormState);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [field]: '',
      general: '',
    }));
  };

  const validateForm = () => {
    const errors = {};
    const parent = (formValues.parent || '').trim();
    if (!parent) {
      errors.parent = 'Parent interface is required';
    }
    const vidNumber = Number(formValues.vid);
    if (!formValues.vid) {
      errors.vid = 'VLAN ID is required';
    } else if (!Number.isInteger(vidNumber) || vidNumber < 1 || vidNumber > 4094) {
      errors.vid = 'VLAN ID must be between 1 and 4094';
    }

    if (formValues.mode === 'static') {
      const ipErr = validateCidr(formValues.ipAddr);
      if (ipErr) {
        errors.ipAddr = ipErr;
      }
      const gatewayErr = validateIpv4(formValues.gateway, true);
      if (gatewayErr) {
        errors.gateway = gatewayErr;
      }
    } else if (formValues.gateway && formValues.gateway.trim()) {
      const gatewayErr = validateIpv4(formValues.gateway);
      if (gatewayErr) {
        errors.gateway = gatewayErr;
      }
    }

    if (
      formValues.priority !== '' &&
      formValues.priority !== null &&
      formValues.priority !== undefined
    ) {
      const priorityNumber = Number(formValues.priority);
      if (!Number.isInteger(priorityNumber) || priorityNumber < 100 || priorityNumber > 899) {
        errors.priority = 'Priority must be between 100 and 899';
      }
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canModify || deviceOffline || submitting) {
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      parent: formValues.parent.trim(),
      vid: Number(formValues.vid),
      mode: formValues.mode || 'static',
    };

    if (formValues.mode === 'static') {
      payload.ipAddr = formValues.ipAddr.trim();
      payload.gateway = formValues.gateway.trim();
    } else if (formValues.gateway.trim()) {
      payload.gateway = formValues.gateway.trim();
    }

    if (
      formValues.priority !== '' &&
      formValues.priority !== null &&
      formValues.priority !== undefined
    ) {
      payload.priority = Number(formValues.priority);
    }

    try {
      setSubmitting(true);
      await onSubmit(payload);
      close();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message || 'Failed to create the VLAN interface.';
      setFormErrors({ general: message });
      NotificationManager.alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  const gatewayLabel =
    formValues.mode === 'static' ? 'Default Gateway' : 'Optional Gateway Override';

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      {deviceOffline && (
        <div style={{ ...styles.banner, ...styles.warningBanner, marginBottom: '16px' }}>
          Device is offline. Configuration changes are disabled until it reconnects.
        </div>
      )}
      {!deviceOffline && !canModify && (
        <div style={{ ...styles.banner, ...styles.warningBanner, marginBottom: '16px' }}>
          You do not have permission to modify device configuration.
        </div>
      )}
      <div className="">
        <h4>Create New VLAN Interface</h4>
        <p style={{ marginTop: 0, marginBottom: '12px', color: '#607284', fontSize: '14px' }}>
          Define VLAN ID, addressing mode, and optional WAN priority.
        </p>
        <TextInput
          label="Parent Interface"
          value={formValues.parent}
          callback={(value) => handleChange('parent', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.parent}
          description="Interfaces follow the <parent>.<vid> convention (eth0.100)."
        />
        <TextInput
          label="VLAN ID"
          type="number"
          min={1}
          max={4094}
          value={formValues.vid}
          callback={(value) => handleChange('vid', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.vid}
        />
        <SelectInput
          label="Addressing Mode"
          value={formValues.mode}
          options={[
            { label: 'Static', value: 'static' },
            { label: 'DHCP', value: 'dhcp' },
          ]}
          callback={(value) => handleChange('mode', value)}
          disabled={!canModify || deviceOffline}
        />
        {formValues.mode === 'static' && (
          <TextInput
            label="Static IP (CIDR)"
            placeholder="192.0.2.10/24"
            value={formValues.ipAddr}
            callback={(value) => handleChange('ipAddr', value)}
            disabled={!canModify || deviceOffline}
            errMsg={formErrors.ipAddr}
          />
        )}
        <TextInput
          label={gatewayLabel}
          placeholder="192.0.2.1"
          value={formValues.gateway}
          callback={(value) => handleChange('gateway', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.gateway}
        />
        <TextInput
          label="WAN Priority (100-899, optional)"
          type="number"
          min={100}
          max={899}
          value={formValues.priority}
          callback={(value) => handleChange('priority', value)}
          disabled={!canModify || deviceOffline}
          errMsg={formErrors.priority}
        />
        {formErrors.general && <div style={styles.dangerText}>{formErrors.general}</div>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
        <button
          type="button"
          className="button secondary"
          style={styles.mutedButton}
          onClick={() => close()}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="button"
          disabled={!canModify || deviceOffline || submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};
